import * as React from "react";

import styles from "./FileRow.module.css";
import { IconBadge } from "../IconBadge/IconBadge";
import { InitialsAvatar } from "../InitialsAvatar/InitialsAvatar";

/** Maps a file name / extension to an icon + tint. */
export const fileExtVisual = (name: string): { icon: string; bg: string; color: string } => {
  const ext = (name.split(".").pop() ?? "").toLowerCase();
  if (ext === "pdf") return { icon: "fa-regular fa-file-pdf", bg: "var(--file-red-bg)", color: "var(--file-red-color)" };
  if (["xls", "xlsx", "csv"].includes(ext)) return { icon: "fa-regular fa-file-excel", bg: "#e6f4ea", color: "#1c8a4d" };
  if (["doc", "docx"].includes(ext)) return { icon: "fa-regular fa-file-word", bg: "#e6effb", color: "#1a5fb4" };
  if (["png", "jpg", "jpeg", "gif", "bmp", "webp"].includes(ext)) return { icon: "fa-regular fa-file-image", bg: "#f0ecfb", color: "#6b4fd0" };
  if (["zip", "rar", "7z"].includes(ext)) return { icon: "fa-regular fa-file-zipper", bg: "#f3f0e6", color: "#8a6d1c" };
  return { icon: "fa-regular fa-file-lines", bg: "#eef0f2", color: "#6e6e73" };
};

export interface FileTableColumns {
  /** css grid-template-columns for header + rows */
  template: string;
  showDate: boolean;
  /** Колонка «Автор» между названием и датой. */
  showAuthor?: boolean;
}

export interface FileRowProps {
  columns: FileTableColumns;
  name: string;
  /** string to derive the file-type icon from (defaults to `name`) */
  extSource?: string;
  /** ФИО автора (CreatedBy.Title). */
  author?: string;
  date?: React.ReactNode;
  onClick?: () => void;
  actionsSlot?: React.ReactNode;
  className?: string;
}

export type FileSortKey = "name" | "author" | "date";
export interface FileSort {
  key: FileSortKey;
  dir: "asc" | "desc";
}

/** Иконка сортировки: активная колонка — стрелка вверх/вниз, иначе нейтральная. */
const sortIcon = (key: FileSortKey, sort?: FileSort | null): string => {
  if (!sort || sort.key !== key) return "fa-solid fa-sort";
  return sort.dir === "asc" ? "fa-solid fa-sort-up" : "fa-solid fa-sort-down";
};

export const FileTableHeader: React.FC<{
  columns: FileTableColumns;
  className?: string;
  /** Текущая сортировка. Передаётся вместе с onSort, чтобы шапка стала кликабельной. */
  sort?: FileSort | null;
  onSort?: (key: FileSortKey) => void;
}> = ({ columns, className, sort, onSort }) => (
  <div
    className={`${styles.header}${className ? ` ${className}` : ``}`}
    style={{ gridTemplateColumns: columns.template }}
  >
    <div
      className={styles.headCell}
      style={onSort ? { cursor: "pointer", userSelect: "none" } : undefined}
      onClick={onSort ? () => onSort("name") : undefined}
    >
      Название файла <i className={sortIcon("name", sort)} />
    </div>
    {columns.showAuthor && (
      <div
        className={styles.headCell}
        style={onSort ? { cursor: "pointer", userSelect: "none" } : undefined}
        onClick={onSort ? () => onSort("author") : undefined}
      >
        Автор <i className={sortIcon("author", sort)} />
      </div>
    )}
    {columns.showDate && (
      <div
        className={styles.headCell}
        style={onSort ? { cursor: "pointer", userSelect: "none" } : undefined}
        onClick={onSort ? () => onSort("date") : undefined}
      >
        Дата создания <i className={sortIcon("date", sort)} />
      </div>
    )}
    {/*<div className={`${styles.headCell} ${styles.right}`}>Действия</div>*/}
  </div>
);

export const FileRow: React.FC<FileRowProps> = ({
  columns, name, extSource, author, date, onClick, actionsSlot, className,
}) => {
  const v = fileExtVisual(extSource ?? name);
  return (
    <div
      className={`${styles.row}${onClick ? ` ${styles.clickable}` : ``}${className ? ` ${className}` : ``}`}
      style={{ gridTemplateColumns: columns.template }}
      onClick={onClick}
    >
      <div className={styles.nameCell}>
        <IconBadge icon={v.icon} bg={v.bg} color={v.color} size={32} />
        <span className={styles.name} title={name}>{name}</span>
      </div>
      {columns.showAuthor && (
        <div className={styles.author}>
          {author ? (
            <React.Fragment>
              <InitialsAvatar name={author} size={26} />
              <span className={styles.authorName} title={author}>{author}</span>
            </React.Fragment>
          ) : (
            <span className={styles.authorEmpty}>—</span>
          )}
        </div>
      )}
      {columns.showDate && <div className={styles.date}>{date}</div>}
      <div className={styles.actions}>{actionsSlot}</div>
    </div>
  );
};
